import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import {
  Address,
  EndOrderTable,
  Order,
  ShippingEstimate,
  ShippingOption,
  ShippingOrder,
  TypesOfShippings,
  formatDuration,
} from '@pedix-workspace/utils';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { getOrderAddressHints } from '@pedix-workspace/shared-models';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { IconMotoZapComponent, IconShippingComponent } from '@pedix-workspace/angular-ui-icons';
import { OrderShippingStatusComponent } from '../../order-shipping-status/order-shipping-status.component';
import { DatePipe, NgClass } from '@angular/common';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { TooltipComponent } from '@pedix-workspace/angular-ui-tooltip';

@Component({
    selector: 'pxw-order-details-shipping-option',
    templateUrl: './order-details-shipping-option.component.html',
    styleUrls: ['./order-details-shipping-option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoDirective,
        ButtonComponent,
        IconMotoZapComponent,
        IconShippingComponent,
        OrderShippingStatusComponent,
        DatePipe,
        NgClass,
        AlertComponent,
        TooltipComponent,
    ]
})
export class OrderDetailsShippingOptionComponent {
  private readonly t = inject(TranslocoService);

  readonly shippingOption = input.required<ShippingOption>();
  readonly label = input<string | null>(null);
  readonly address = input.required<Address | null>();
  readonly shippingEstimate = input.required<ShippingEstimate | null>();
  readonly shippingOrder = input.required<ShippingOrder | null>();
  readonly tableNumber = input.required<string | null>();
  readonly table = input<EndOrderTable | null>(null);
  readonly canViewTableSession = input(false);

  readonly displayShippingEstimate = input(false);
  readonly isShippingRequestInProgress = input(false);
  readonly isOndemandShippingRequestInProgress = input(false);
  readonly isCancelShippingRequestInProgress = input(false);
  readonly isDedicatedService = input(false);

  readonly confirmShipping = output();
  readonly createShipping = output();
  readonly createOndemandShipping = output();
  readonly cancelShipping = output();
  readonly viewTableSession = output();

  readonly serviceDuration = computed(() => {
    const table = this.table();

    if (!table?.openedAt || !table.closedAt) {
      return null;
    }
    return formatDuration(
      (table.closedAt.getTime() - table.openedAt.getTime()) / 60_000,
      (key, params) => this.t.translate(key, params),
    );
  });

  readonly isDelivery = computed(
    () =>
      this.shippingOption().type === TypesOfShippings.DELIVERY ||
      this.shippingOption().type === TypesOfShippings.PARCEL,
  );
  readonly shippingAddress = computed(() => {
    const address = this.address();

    if (!address) {
      return null;
    }
    return (address.addressDetails ? address.addressDetails.fullAddress : address.street) || null;
  });
  readonly addressLink = computed(() => {
    return this.address()?.addressDetails?.url;
  });
  readonly addressHints = computed((): string | null => {
    const address = this.address();

    if (!address) {
      return null;
    }
    return getOrderAddressHints(address, this.shippingOption().type);
  });

  readonly canCancelShipping = computed((): boolean => {
    const shippingOrder = this.shippingOrder();
    if (!shippingOrder) {
      return false;
    }

    if (shippingOrder.providerName === 'correoargentino') {
      return false;
    }

    const CANCELABLE_STATUSES = {
      pedidosya: ['CONFIRMED'],
      puniya: ['CONFIRMED'],
      rapiboy: ['CONFIRMED', 'IN_PROGRESS'],
      cabify: ['CONFIRMED'],
    } as Record<string, string[]>;

    return (
      CANCELABLE_STATUSES[shippingOrder.providerName]?.includes(shippingOrder.status ?? '') || false
    );
  });

  readonly agencyInfo = computed(() => {
    const shippingEstimate = this.shippingEstimate();
    if (!shippingEstimate?.additionalInfo || shippingEstimate.deliveryType !== 'S') {
      return null;
    }
    try {
      const parsed = JSON.parse(shippingEstimate.additionalInfo);
      return parsed.agencyInfo as {
        id: string;
        name: string;
        address: string;
        city: string;
        state: string;
        schedule?: string;
      } | null;
    } catch {
      return null;
    }
  });

  readonly shippingButtonText = computed(() => {
    const shippingEstimate = this.shippingEstimate();
    const isDedicated = this.isDedicatedService();

    if (!shippingEstimate) {
      return isDedicated ? 'Crear solicitud de envío' : 'Solicitar envío';
    }

    const isCorreoArgentino = shippingEstimate.providerName === 'correoargentino';
    const hasOfferId = !!shippingEstimate.offerId;

    if (isCorreoArgentino) {
      return hasOfferId ? 'Confirmar sincronización' : 'Sincronizar pedido';
    }

    if (isDedicated) {
      return 'Crear solicitud de envío';
    }

    return hasOfferId ? 'Confirmar envío' : 'Solicitar envío';
  });

  readonly isPrimaryButtonDisabled = computed(() => {
    return this.isOndemandShippingRequestInProgress();
  });

  readonly isSecondaryButtonDisabled = computed(() => {
    return this.isShippingRequestInProgress();
  });
}
